import Papa from 'papaparse';

import { DATE_COLUMN_HINT, LE_FLASH_COLUMN_ALIASES, REPORT_DATE_HINT } from './constants';
import { OtaCsvRow, ParsedOtaCsv } from './types';

/** Prefers a date-like column that isn't also a "report date" (scrape-run date). */
function detectDateColumn(columns: Array<string>): string {
  const dateCols = columns.filter((c) => c.toLowerCase().includes(DATE_COLUMN_HINT));
  const nonReport = dateCols.filter((c) => !c.toLowerCase().includes(REPORT_DATE_HINT));
  return nonReport[0] ?? dateCols[0] ?? columns[0];
}

function detectLeFlashColumn(columns: Array<string>, dateColumn: string): string | null {
  const candidates = columns.filter((c) => c !== dateColumn);
  const lowerAliases = LE_FLASH_COLUMN_ALIASES;
  return candidates.find((c) => lowerAliases.includes(c.toLowerCase())) ?? null;
}

/** Blank/non-numeric/non-positive cells are `null`, never 0 — LOW/AVERAGE must skip them, not treat them as 0. */
function parsePositiveNum(val: string | undefined | null): number | null {
  if (val == null || val.trim() === '') {
    return null;
  }
  const n = Number(val);
  return isNaN(n) || n <= 0 ? null : n;
}

/** Parses an OTA rate-shop CSV of arbitrary width — every non-date, non-LE-flash column is a competitor OTA channel. */
export function parseOtaCsv(csvText: string): ParsedOtaCsv {
  const parsed = Papa.parse<Record<string, string>>(csvText, { header: true, dynamicTyping: false, delimiter: ',' });
  // TooFewFields fires on trailing blank lines Papa still counts as rows — harmless, filtered out below.
  const realErrors = parsed.errors.filter((e) => e.code !== 'TooFewFields');
  if (realErrors.length > 0) {
    console.warn('parseOtaCsv: CSV parse errors', realErrors);
  }
  const rawRows = parsed.data.filter((r) => Object.values(r).some((v) => v !== '' && v != null));

  if (rawRows.length === 0) {
    return { dateColumn: '', leFlashColumn: null, otaColumns: [], rows: [] };
  }

  const columns = Object.keys(rawRows[0]);
  const dateColumn = detectDateColumn(columns);
  const leFlashColumn = detectLeFlashColumn(columns, dateColumn);
  // Excludes every date-like column (e.g. a separate "Report Date" scrape-run column alongside the
  // stay date), not just the chosen `dateColumn` — matches the mockup's `ota` column filter.
  const otaColumns = columns.filter((c) => c !== leFlashColumn && !c.toLowerCase().includes(DATE_COLUMN_HINT));

  const rows: Array<OtaCsvRow> = [];
  for (const row of rawRows) {
    const date = row[dateColumn];
    if (!date) {
      continue;
    }

    const otaRates: Record<string, number | null> = {};
    for (const col of otaColumns) {
      otaRates[col] = parsePositiveNum(row[col]);
    }

    rows.push({
      date,
      otaRates,
      leFlashRate: leFlashColumn ? parsePositiveNum(row[leFlashColumn]) : null,
    });
  }

  rows.sort((a, b) => a.date.localeCompare(b.date));

  return { dateColumn, leFlashColumn, otaColumns, rows };
}
